import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';
import {
  AlertTriangle,
  Check,
  Cloud,
  Clapperboard,
  LayoutTemplate,
  Loader2,
  Rocket,
  Rss,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogClose, Button, cn } from '@scio3/ui';
import { DOWNGRADE_TRIAL_TO_FREE } from '@scio3/graphql-client';
import { useAppVariant } from '@/lib/app-variant';
import { useAuthStore } from '@/stores/auth-store';
import { isBillingExperimentOn } from '../experiment/is-billing-experiment-on';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { useBillingData } from '../hooks/useBillingData';
import { useRecordCancelFlowDowngrade } from '../hooks/useRecordCancelFlowDowngrade';
import { extractMutationResponse } from '../utils/plan-helpers';
import { PAYMENT_STATUS_CODE_LIMIT_QUANTITY } from '../utils/billing-constants';
import { postSubscribeLandingPath } from '../experiment/wizard/post-subscribe-landing';
import { stashPostSubscribeToast } from '../experiment/wizard/post-subscribe-handoff';

type Cell = { type: 'check' } | { type: 'x' } | { type: 'dash' } | { type: 'text'; value: string };

type ComparisonRow = {
  icon: LucideIcon;
  iconClassName?: string;
  labelKey: string;
  standard: Cell;
  free: Cell;
};

const ROWS: ComparisonRow[] = [
  {
    icon: Rocket,
    labelKey: '100+ Apps & Integrations',
    standard: { type: 'check' },
    free: { type: 'text', value: '25 Apps' },
  },
  {
    icon: Clapperboard,
    labelKey: 'Build playlist + schedule content',
    standard: { type: 'check' },
    free: { type: 'dash' },
  },
  {
    icon: LayoutTemplate,
    labelKey: 'Ready to use Templates',
    standard: { type: 'check' },
    free: { type: 'dash' },
  },
  {
    icon: Rss,
    labelKey: 'Feeds',
    standard: { type: 'check' },
    free: { type: 'dash' },
  },
  {
    icon: Cloud,
    labelKey: 'OptiSigns Logo on screens',
    standard: { type: 'dash' },
    free: { type: 'x' },
  },
  {
    icon: AlertTriangle,
    iconClassName: 'text-foreground',
    labelKey: 'Maximum of 3 Screens',
    standard: { type: 'dash' },
    free: { type: 'x' },
  },
  {
    icon: AlertTriangle,
    iconClassName: 'text-foreground',
    labelKey: '25 MB file limit',
    standard: { type: 'dash' },
    free: { type: 'x' },
  },
];

function CellRender({ cell, labelKey }: { cell: Cell; labelKey: string }) {
  const { t } = useTranslation();
  switch (cell.type) {
    case 'check':
      return <Check className="h-4 w-4 text-primary" aria-label={t('Included')} />;
    case 'x':
      return (
        <X className="h-4 w-4 text-destructive" aria-label={`${t(labelKey)} — not included`} />
      );
    case 'dash':
      return <span className="text-xs text-muted-foreground">-</span>;
    case 'text':
      return <span className="text-xs font-medium">{t(cell.value)}</span>;
  }
}

export function DowngradeComparisonDialog() {
  const { t } = useTranslation();
  const open = useBillingDialogStore((s) => s.downgradeComparisonOpen);
  const ctx = useBillingDialogStore((s) => s.downgradeComparisonContext);
  const close = useBillingDialogStore((s) => s.closeDowngradeComparison);
  const openDowngrade = useBillingDialogStore((s) => s.openDowngrade);
  const openScreenManagement = useBillingDialogStore((s) => s.openScreenManagement);

  const { isTrial, currency } = useBillingData();
  const subAccountId = ctx.subAccountId as string | undefined;
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  const closeWizard = useBillingDialogStore((s) => s.closeDialog);
  const recordCancelFlowDowngrade = useRecordCancelFlowDowngrade();
  /** #2046 — the landing path for the post-downgrade document load. */
  const variant = useAppVariant();

  const [downgradeTrialToFree, { loading: trialLoading }] = useMutation(DOWNGRADE_TRIAL_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });

  const handleConfirm = useCallback(async () => {
    // Paid → stack DowngradeDialog ON TOP of the comparison (do NOT close the comparison).
    // DowngradeDialog will close the comparison itself when the user confirms / on SP53 handoff.
    if (!isTrial) {
      openDowngrade({ subAccountId, warnings: [] });
      return;
    }

    // Trial → no second confirm (scio1 parity for trial flow); fire mutation directly.
    try {
      const result = await downgradeTrialToFree({
        variables: { subAccountId, currency },
      });
      const response = extractMutationResponse<{
        status?: boolean;
        message?: string;
        code?: number;
      }>(result.data, 'downgradeTrialToFreePlan');
      if (response?.status) {
        recordCancelFlowDowngrade({ targetPlan: 'free', viaTrial: true });
        const confirmation = t('Your plan has been downgraded to Free');
        close();
        closeWizard();
        /**
         * #2046 — `assign(landing)`, NOT `reload()`.
         *
         * The wizard is addressed by `#pricing`, and `closeWizard()` only flips store state — the
         * effect in `useWizardUrlHash` that strips the hash from the URL never gets to run before
         * the navigation. `reload()` therefore re-requested a URL that still carried `#pricing`,
         * the fresh document saw it and re-opened the wizard with no context, and a user who had
         * just deliberately declined to pay was dropped back onto the paid plan cards with
         * Standard pre-selected — reading as if the downgrade had failed. It had not.
         *
         * This is the same remedy the paid subscribe path already documents at
         * `useSubscriptionWizard.handleOpenChange`; the Free path never picked it up.
         *
         * The toast is stashed rather than fired: the navigation below destroys any toast raised
         * here, which is why the downgrade used to confirm itself to nobody. It is re-raised on
         * the fresh document by `BillingDialogs`.
         */
        /**
         * ARM `b` ONLY. The bug this fixes (#2046) is arm b's own: its wizard writes `#pricing`
         * into the URL (`useWizardUrlHash`), so a `reload()` re-requests a URL that re-opens the
         * plan chooser over an account that just left the paid tier. Control never writes that
         * hash, so `reload()` was always correct there — and gating on the arm is what keeps a
         * lapsed CONTROL session byte-identical to main.
         */
        if (isAccountInactive && isBillingExperimentOn()) {
          stashPostSubscribeToast(confirmation);
          window.location.assign(postSubscribeLandingPath(variant));
          return;
        }
        toast(confirmation);
        return;
      }
      // #1362 — SP53 / LIMIT_QUANTITY: trial user has an unsupported device type
      // (Roku / Firestick / Tizen / iOS / etc.). Stack ScreenManagementDialog ON TOP
      // of this dialog (and the wizard underneath). onContinue re-fires handleConfirm
      // so the dialog's "Downgrade to Free" primary action retries the trial-downgrade
      // mutation in-place once devices are cleared.
      if (response?.code === PAYMENT_STATUS_CODE_LIMIT_QUANTITY) {
        openScreenManagement({
          subAccountId,
          messageKey: 'screenManagementModalFreePlan',
          onContinue: () => handleConfirm(),
        });
        return;
      }
      toast.error(
        response?.message
          ? t(response.message, { defaultValue: response.message })
          : t('Failed to downgrade'),
      );
    } catch (err) {
      console.error('Downgrade failed:', err);
      toast.error(t('Failed to downgrade'));
    }
  }, [
    isTrial,
    close,
    openDowngrade,
    openScreenManagement,
    subAccountId,
    downgradeTrialToFree,
    currency,
    closeWizard,
    isAccountInactive,
    variant,
    recordCancelFlowDowngrade,
    t,
  ]);

  const isSubmitting = trialLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        data-testid="downgrade-comparison-dialog"
      >
        <div className="px-6 pr-8 pt-6">
          <DialogTitle className="text-xl font-semibold">
            {t('Are you sure you want to downgrade?')}
          </DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("If you downgrade now here's what will change")}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pt-3 pb-1">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card pb-4 pt-2">
            {/* Light-gray highlight strip behind the Free column — absolute so
                it spans the card's full height through the py-4 padding. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-[88px] bg-muted"
            />
            <div className="relative">
              <div className="grid grid-cols-[1fr_88px_88px] items-stretch gap-x-1 pl-8 text-sm font-semibold">
                <span />
                <span className="py-2 pr-1 text-center">{t('Standard+')}</span>
                <span className="flex items-center justify-center px-2 py-2">{t('Free')}</span>
              </div>

              {ROWS.map((row) => {
                const Icon = row.icon;
                return (
                  <div
                    key={row.labelKey}
                    className="grid grid-cols-[1fr_88px_88px] items-stretch gap-x-1 pl-8 text-sm"
                  >
                    <div className="flex items-center gap-2 py-2 pr-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                        <Icon className={cn('h-4 w-4 text-foreground', row.iconClassName)} />
                      </span>
                      <span className="text-foreground">{t(row.labelKey)}</span>
                    </div>
                    <div className="flex items-center justify-center py-2 pr-1">
                      <CellRender cell={row.standard} labelKey={row.labelKey} />
                    </div>
                    <div className="flex items-center justify-center px-2 py-2">
                      <CellRender cell={row.free} labelKey={row.labelKey} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 bg-card px-6 py-4">
          <Button
            variant="outline"
            onClick={handleConfirm}
            disabled={isSubmitting}
            data-testid="downgrade-comparison-confirm-btn"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('Yes, downgrade to free plan')}
          </Button>
          <DialogClose asChild>
            <Button
              data-testid="downgrade-comparison-keep-btn"
              className="cursor-pointer"
              disabled={isSubmitting}
            >
              {t('Choose a different plan')}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
